import { z } from 'zod';
import { ProviderTraceSchema } from './provider-contracts';
import type { Routing } from './provider-contracts';

export const BUDGET_CAP = 100_000_000; // Integer micro-USD. Never floating-point credits.
export const PRICING_VERSION = 'google-2026-09-28';
export const MODELS = {
  text: 'gemini-3.8-flash',
  image: 'gemini-3.1-flash-image',
  video: 'gemini-omni-1.1-flash',
  controlledVideo: 'veo-3.1-generate-preview',
  speech: 'gemini-3.1-flash-tts-preview',
  transcription: 'gemini-3.5-transcribe',
  // Approved by the owner on September 28, 2026: one instrumental score per episode.
  music: 'lyria-3.5',
} as const;
export type Role = 'owner' | 'editor' | 'reviewer';
export type Phase =
  'IDEA' | 'PITCH' | 'BLUEPRINT' | 'CASTING' | 'PRODUCTION' | 'REVIEW' | 'ACCEPTED';
export const jobKinds = [
  'PITCH',
  'SEASON',
  'CAST_IMAGE',
  'SCRIPT',
  'REFERENCE',
  'QA_REFERENCE',
  'VIDEO',
  'QA_VIDEO',
  'QA_CONTENT',
  'CAPTIONS',
  'ASSEMBLE',
  'REVISION',
  'SOFTEN',
  'SPEECH',
  'SCRIPT_DOCTOR',
  'SCRIPT_REVIEW',
  'EDIT_PLAN',
  'SET_IMAGE',
  'QA_SET',
  'SCORE',
  'EPISODE_REVIEW',
  // Pipeline 2: a writers' room and a director instead of one writer filling one large plan.
  'STORY',
  'PUNCH_UP',
  'PANEL',
  'BREAKDOWN',
  // Pipeline 2: lip sync, words and acting, watched with sound; QA_VIDEO judges the picture.
  'QA_PERFORMANCE',
] as const;
export type JobKind = (typeof jobKinds)[number];
/** The jobs only Pipeline 2 runs, and the jobs only Pipeline 1 runs. */
export const PIPELINE2_JOBS = new Set<JobKind>([
  'STORY',
  'PUNCH_UP',
  'PANEL',
  'BREAKDOWN',
  'QA_PERFORMANCE',
]);
export const PIPELINE1_JOBS = new Set<JobKind>([
  'SCRIPT',
  'SCRIPT_DOCTOR',
  'SCRIPT_REVIEW',
  'REVISION',
  'EDIT_PLAN',
]);
/** Optional finishing failures retain paid footage. An unavailable script review retains the script but blocks new filming until reviewed. */
export const optionalJobs = new Set<JobKind>([
  'SCRIPT_DOCTOR',
  'SCRIPT_REVIEW',
  'EDIT_PLAN',
  'CAPTIONS',
  'SET_IMAGE',
  'QA_SET',
  'SCORE',
  'EPISODE_REVIEW',
  'PUNCH_UP',
]);
export const commandKinds = [
  'DEVELOP',
  'REVISE_PITCH',
  'PLAN_SEASON',
  'CAST_ACTOR',
  'PLAN_EPISODE',
  'PRODUCE_EPISODE',
  'REGENERATE_SHOT',
  'REVISE_EPISODE',
  'ASSEMBLE_EPISODE',
  'ADD_NARRATION',
] as const;
export type CommandKind = (typeof commandKinds)[number];
export const terminalJobs = new Set(['SUCCEEDED', 'FAILED', 'CANCELLED', 'RECONCILIATION']);
export const CharacterSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  age: z.number().int().min(18),
  role: z.string(),
  personality: z.string(),
  motivation: z.string(),
  backstory: z.string(),
  arc: z.string(),
  appearance: z
    .string()
    .describe(
      'Opens with the physical features that tell this person apart at a glance — face, hair, build, apparent age, permanent marks — never clothing.',
    ),
  voice: z.string().describe('Pitch, timbre, pace and accent, in a way no other character shares.'),
  /**
   * Someone only ever heard — on a phone, a voicemail, a recording — never seen. They need a voice
   * but no actor, cost nothing to cast, and speak only as an offscreen or device voice. Without
   * this, a story whose twist is an old voicemail could not give its sender a line.
   */
  voiceOnly: z
    .boolean()
    .optional()
    .describe(
      'True for someone only ever heard — on a phone, a voicemail, a recording — and never seen.',
    ),
});
export type Character = z.infer<typeof CharacterSchema>;
export const PitchSchema = z.object({
  title: z.string().min(1).max(120),
  synopsis: z.string().min(20).max(1500),
  genre: z.string(),
  tone: z.string(),
  conflict: z.string(),
  dramaticEngine: z.string(),
  ending: z.string(),
  /**
   * As many people as the premise needs, and a floor of one rather than two.
   *
   * The floor used to be two, and this schema is sent to the provider as the response contract,
   * so "between two and four" was a constraint before it was ever a suggestion — the model
   * settled on three every time. A creator who asked for stand-up, where the whole format is one
   * performer alone on a stage, was given a comedian and two people who do not exist in it, then
   * asked to cast all three. The upper bound is left where it is: every character is a generated
   * identity reference that the creator's allowance pays for.
   */
  characters: z.array(CharacterSchema).min(1).max(4),
});
export type Pitch = z.infer<typeof PitchSchema>;
/**
 * A CLAPTAL Kids pitch: the same contract, with characters of any age. Children, animals and
 * robots are the cast of a children's series; every drama keeps its adults-only floor above.
 */
export const KidsPitchSchema = PitchSchema.extend({
  characters: z
    .array(
      CharacterSchema.extend({
        age: z
          .number()
          .int()
          .min(0)
          .max(1000)
          .describe('Age in years; a child, an animal, a robot or a grown-up.'),
      }),
    )
    .min(1)
    .max(4),
});
/** The pitch contract for a project, by its audience. */
export const pitchSchemaFor = (audience?: unknown) =>
  audience === 'kids' ? KidsPitchSchema : PitchSchema;
/** The series look the edit applies to every shot, so clips from separate generations read as one film. */
export const GRADES = ['neutral', 'warm', 'cool', 'noir', 'pastel'] as const;
export type Grade = (typeof GRADES)[number];
export const SeasonSchema = z.object({
  storyBible: z.object({
    premise: z.string(),
    world: z.string(),
    timeline: z.string(),
    themes: z.array(z.string()),
    rules: z.array(z.string()),
    seasonArc: z.string(),
    ending: z.string(),
  }),
  visualBible: z.object({
    style: z.string(),
    lighting: z.string(),
    cameraLanguage: z.string(),
    palette: z.array(z.string()),
    locations: z.array(z.string()),
    // Optional so every season planned before these existed still parses. The edit falls back
    // to a neutral look and the score to the genre and tone.
    grade: z.enum(GRADES).optional(),
    score: z
      .object({
        instruments: z.string(),
        motif: z.string(),
        tempo: z.string().describe('The tempo feel, e.g. "slow, patient pulse".'),
        bpm: z.number().int().min(40).max(200).optional().describe('Beats per minute.'),
        key: z.string().optional().describe('Musical key, e.g. "D minor".'),
      })
      .optional(),
  }),
  episodes: z
    .array(
      z.object({
        number: z.number().int().positive(),
        title: z.string(),
        synopsis: z.string(),
        cliffhanger: z.string(),
      }),
    )
    .min(5)
    .max(12),
  canon: z.array(
    z.object({
      subject: z.string(),
      predicate: z.string(),
      value: z.string(),
      validFrom: z.number().int().min(1),
      knownFrom: z.number().int().min(1),
    }),
  ),
});
export type Season = z.infer<typeof SeasonSchema>;
export const MIN_EPISODE_SHOTS = 3;
export const MAX_EPISODE_SHOTS = 14;
export const MAX_SCENE_CAMERAS = 8;
export const MAX_TAKE_SECONDS = 30;
export const MAX_TAKE_SOURCE_SECONDS = 31;
const DialogueLineSchema = z.object({
  characterId: z.string(),
  text: z.string().describe('Exactly what is said. Short, in character, subtext over statement.'),
  audioSource: z
    .enum(['onscreen', 'offscreen', 'device'])
    .optional()
    .describe('device for recorded speech, offscreen for an unseen live speaker; omit otherwise.'),
  /**
   * How the line is played: volume, pace, feeling, where the speaker looks. Optional so older
   * screenplays parse; the video model otherwise hears only the character's standing voice.
   */
  delivery: z
    .string()
    .optional()
    .describe(
      'How the line is played: volume, pace, feeling and where the speaker looks, e.g. "barely above a whisper, not looking at him".',
    ),
});

/**
 * Pipeline 2 films a sequence — one generation of 4–15 seconds containing its own cuts — rather
 * than one shot per generation. How fast it cuts follows what the moment is for: a confrontation
 * cuts on every new piece of information, a confession is held in one shot. Speed is a result of
 * the drama, never a quota; a held moment chopped into fragments is as wrong as a fight filmed in
 * one static frame.
 */
export const TEMPOS = ['charged', 'measured', 'held'] as const;
export type Tempo = (typeof TEMPOS)[number];
/** H3's own camera vocabulary: motion type, with amplitude and speed only when they matter. */
export const CAMERA_MOTIONS = [
  'static',
  'push in',
  'pull out',
  'zoom in',
  'zoom out',
  'pan left',
  'pan right',
  'truck left',
  'truck right',
  'tilt up',
  'tilt down',
  'pedestal up',
  'pedestal down',
  'arc',
  'tracking',
  'handheld',
] as const;
export const CutSchema = z.object({
  at: z
    .number()
    .nonnegative()
    .describe('Seconds from the start of the sequence where this cut begins; the first is 0.'),
  duration: z.number().positive().describe('How long this cut stays on screen, in seconds.'),
  view: z
    .string()
    .min(1)
    .describe("The scene camera this cut is seen from: a key of the scene's cameras."),
  framing: z
    .string()
    .min(1)
    .describe(
      'Shot size and composition, e.g. "medium close-up of Sara, Leo soft behind her right shoulder".',
    ),
  cameraMotion: z.object({
    type: z.enum(CAMERA_MOTIONS),
    amplitude: z.enum(['small', 'large']).optional(),
    speed: z.enum(['slow', 'fast']).optional(),
  }),
  visible: z
    .array(
      z.object({
        characterId: z.string(),
        side: z.enum(['left', 'center', 'right']),
        depth: z.enum(['foreground', 'midground', 'background']),
        facing: z.string().min(1).describe('Lower-case phrase continuing "body turned …".'),
        action: z
          .string()
          .min(1)
          .describe('What this person visibly does during the cut, including where they look.'),
      }),
    )
    .describe('Everyone seen in this cut, at most three, and where in the frame.'),
  dialogue: z.array(DialogueLineSchema).describe('The lines said during this cut, in order.'),
  reaction: z
    .string()
    .optional()
    .describe('What the listener does while and after a line lands in this cut.'),
  sound: z.string().optional().describe('A specific diegetic sound in this cut, never music.'),
  newInformation: z
    .string()
    .min(1)
    .describe(
      'What this cut shows that the previous one did not: a person, a place, a change of state, a viewpoint or a moment in time.',
    ),
});
export type Cut = z.infer<typeof CutSchema>;
export const StagingSchema = z.object({
  present: z
    .array(
      z.object({
        characterId: z.string(),
        mark: z
          .string()
          .describe("Where they are at the sequence's first frame: a mark ID of the scene."),
        posture: z
          .string()
          .min(1)
          .describe('e.g. "seated on the bed edge", "standing, arms crossed".'),
        holding: z
          .string()
          .optional()
          .describe('What is in their hands at the start, if anything.'),
      }),
    )
    .describe('Everyone physically in the place at the start of the sequence, seen or not.'),
  objects: z
    .array(z.object({ id: z.string(), state: z.string().min(1) }))
    .describe('The state of every changeable prop of the scene at the start, e.g. door: closed.'),
  events: z
    .array(
      z.object({
        at: z.number().nonnegative(),
        kind: z.enum(['enter', 'exit', 'move', 'object']),
        characterId: z.string().optional(),
        mark: z.string().optional().describe('Where the person ends up, for enter and move.'),
        object: z.string().optional(),
        state: z.string().optional(),
        cause: z
          .string()
          .min(1)
          .describe('Who does it and how, e.g. "Leo pushes the door open with his shoulder".'),
      }),
    )
    .describe('Every entrance, exit, move between marks and change to a prop, in time order.'),
});
export type Staging = z.infer<typeof StagingSchema>;

export const ShotSchema = z.object({
  key: z.string(),
  sceneKey: z.string(),
  order: z.number().int().nonnegative(),
  duration: z.number().min(3).max(MAX_TAKE_SECONDS),
  generationDuration: z.number().min(3).max(MAX_TAKE_SOURCE_SECONDS).optional(),
  purpose: z.string().describe('What this shot does for the story. Never shown to a camera.'),
  characterIds: z
    .array(z.string())
    .describe('Everyone visible in the shot, and nobody else. At most two clearly visible.'),
  location: z.string().describe("Exactly the scene's location."),
  wardrobe: z
    .string()
    .describe("Clothing summary for this shot; the scene's `outfits` are what cameras use."),
  physicalState: z
    .string()
    .describe(
      'Props in hand and bodily state at the start, e.g. "Maya holds the key in her left hand".',
    ),
  emotion: z.string().describe('What is felt; `action` says what the body does about it.'),
  action: z
    .string()
    .describe('What visibly happens, as physical behaviour a camera can see, in order.'),
  dialogue: z.array(DialogueLineSchema),
  /** Consecutive performance beats inside one uninterrupted take, never editorial cuts. */
  takeBeats: z
    .array(
      z.object({
        duration: z.number().min(3).max(9.5),
        action: z.string().min(1),
        camera: z.string().min(1),
        dialogue: z.array(DialogueLineSchema),
      }),
    )
    .min(2)
    .max(4)
    .optional()
    .describe('Leave out. Only for existing extended takes longer than ten seconds.'),
  /** What happens after the last spoken line until the cut: the reaction, a movement, a look. */
  afterLines: z
    .string()
    .optional()
    .describe('What happens between the last line and the cut: the reaction, a movement, a look.'),
  framing: z.string().describe('Shot size, e.g. "comfortable medium two-shot".'),
  movement: z.string(),
  lighting: z
    .string()
    .describe(
      "Light in this shot within the scene's conditions — direction and quality, never a different time of day or weather.",
    ),
  continuity: z.array(z.string()),
  cameraKey: z.string().min(1).optional(),
  cameraMove: z
    .object({
      kind: z.enum(['hold', 'pan', 'tilt', 'dolly', 'truck', 'arc', 'follow', 'rise']),
      endCameraKey: z
        .string()
        .min(1)
        .optional()
        .describe(
          'Required when the camera moves: a different camera of the same scene with exactly the same lens. Omit for a hold.',
        ),
      path: z.string().min(1),
      focus: z.string().min(1),
      pace: z.string().min(1),
      motivation: z.string().min(1),
    })
    .optional(),
  blocking: z
    .array(
      z.object({
        characterId: z.string(),
        start: z
          .string()
          .min(1)
          .describe('Lower-case phrase continuing "starts …", relative to a named anchor.'),
        end: z.string().min(1).describe('Lower-case phrase continuing "ends …".'),
        facing: z.string().min(1).describe('Lower-case phrase continuing "body turned …".'),
        gaze: z
          .string()
          .min(1)
          .describe('Lower-case phrase continuing "eyes …", naming whom or what they watch.'),
        addresses: z.string().optional().describe('The character ID spoken to, when speaking.'),
        response: z
          .string()
          .min(1)
          .describe('Lower-case phrase continuing "while listening, …": a visible reaction.'),
      }),
    )
    .optional(),
  cutReason: z.string().min(1).optional(),
  beat: z.enum(['hook', 'dialogue', 'reaction', 'turn', 'cliffhanger']).optional(),
  /**
   * Initial screen positions. Motivated movement may change them; blocking describes the path.
   */
  screenPositions: z
    .array(z.object({ characterId: z.string(), position: z.enum(['left', 'center', 'right']) }))
    .optional(),
  /** Ambience and effects, never music: the score is laid under the whole episode in the edit. */
  sound: z.string().optional().describe('Ambience and specific effects only, never music.'),
  /**
   * Set by the backend, never by a model. `set` preserves the legacy location label;
   * `trimIn` is how much of the start of the clip the
   * edit skips, so the cut lands on movement rather than on the still first frame the clip was
   * animated from.
   */
  set: z.string().optional(),
  trimIn: z.number().min(0).max(1).optional(),
  /** Pipeline 2: the sequence's tempo, its cuts, who is where, and how it is generated. */
  tempo: z.enum(TEMPOS).optional(),
  cuts: z.array(CutSchema).optional(),
  staging: StagingSchema.optional(),
  /** `reference`: cast and room as references, cuts inside; `first-frame`: one held shot. */
  generationMode: z.enum(['reference', 'first-frame']).optional(),
  /** The state this sequence ends in, which the next one starts from. */
  handoff: z.string().optional(),
});
export type ShotSpec = z.infer<typeof ShotSchema>;
/** How many words a second a line may ask of a shot before it cannot be said in the time it has. */
export const MAX_WORDS_PER_SECOND = 3;
export const wordsPerSecond = (shot: Pick<ShotSpec, 'dialogue' | 'duration'>) =>
  shot.dialogue.reduce((n, line) => n + line.text.split(/\s+/).filter(Boolean).length, 0) /
  shot.duration;
/** The fields a model writes. Anything else on a shot belongs to the backend and is restored. */
export const BACKEND_SHOT_FIELDS = [
  'generationDuration',
  'trimIn',
  'set',
  'generationMode',
] as const;
/** The largest clip the generator is asked for, and the start the edit skips when it can. */
export const MAX_GENERATION_SECONDS = 10;
export const HANDLE_SECONDS = 0.5;
/**
 * A shot with room at the start of its clip for the edit to cut past.
 *
 * The clip is animated from a still frame, so its first instant is always motionless, and a hard
 * cut into stillness reads as a stutter. Half a second more is generated at the start and skipped
 * in the edit, and the same half second of sound is what lets a new scene be heard just before it
 * is seen. Generation is asked for whole seconds, so a six-and-a-half-second shot costs exactly
 * its handle and a whole-second one costs a little more; the screenplay guide asks for halves.
 */
export function withHandles(shot: ShotSpec, set?: string): ShotSpec {
  const generation = Math.min(
    shot.duration > MAX_GENERATION_SECONDS ? MAX_TAKE_SOURCE_SECONDS : MAX_GENERATION_SECONDS,
    Math.max(shot.generationDuration ?? 0, Math.ceil(shot.duration + HANDLE_SECONDS)),
  );
  const trimIn = Math.max(0, Math.min(HANDLE_SECONDS, generation - shot.duration));
  return defined({
    ...shot,
    generationDuration: generation,
    trimIn: Math.round(trimIn * 100) / 100,
    set: set ?? shot.set,
  });
}
/**
 * A model's rewrite of a shot, with what the backend set on it put back.
 *
 * A rewording is asked for the whole shot and routinely leaves out fields it was not asked to
 * think about. The handle and the set are not the model's: dropping them would silently change
 * how the shot is generated and which plate it is drawn against.
 */
export function keepBackendFields(before: ShotSpec, after: ShotSpec): ShotSpec {
  return defined({
    ...after,
    generationDuration: before.generationDuration,
    trimIn: before.trimIn,
    set: before.set,
    generationMode: before.generationMode,
    tempo: after.tempo ?? before.tempo,
    cuts: after.cuts ?? before.cuts,
    staging: after.staging ?? before.staging,
    handoff: after.handoff ?? before.handoff,
    cameraKey: after.cameraKey ?? before.cameraKey,
    cameraMove: after.cameraMove ?? before.cameraMove,
    takeBeats:
      after.duration > MAX_GENERATION_SECONDS
        ? (after.takeBeats ?? before.takeBeats)
        : after.takeBeats,
    blocking: after.blocking ?? before.blocking,
    cutReason: after.cutReason ?? before.cutReason,
    beat: after.beat ?? before.beat,
  });
}
/** Without the keys whose value is `undefined`, which a stored document cannot hold. */
export function defined<T extends object>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;
}
/**
 * What a scene's time and weather look like, the same in every shot of it. Shots used to carry
 * their light one by one, and an interior that opened in morning light could show night through
 * its window by the next shot. Time and weather change only with a new scene.
 */
export const SceneConditionsSchema = z.object({
  timeOfDay: z.string().min(1).describe('When the scene happens, e.g. "late morning", "night".'),
  weather: z
    .string()
    .min(1)
    .describe(
      'Outside, as seen through windows or on location, e.g. "bright sun, clear sky", "steady rain, grey overcast"; "not visible" when no exterior shows.',
    ),
  light: z
    .string()
    .min(1)
    .describe(
      'How the scene is lit at that moment: which lamps are on or off and the quality of light, e.g. "hard sunlight through the north window, all lamps off".',
    ),
});
export const SceneSchema = z.object({
  key: z.string(),
  title: z.string(),
  location: z.string(),
  summary: z.string(),
  conditions: SceneConditionsSchema.optional().describe(
    'Fixed for every shot of the scene; a different time or weather needs a new scene.',
  ),
  /**
   * What each person wears for the whole scene. Clothes were written shot by shot, so nothing
   * stopped a coat from coming and going between two shots of the same moment.
   */
  outfits: z
    .array(z.object({ characterId: z.string(), outfit: z.string() }))
    .optional()
    .describe(
      'One entry per person seen in the scene: the garments they wear in every shot of it. New clothes need a new scene.',
    ),
  space: z
    .object({
      layout: z.string().min(1),
      /**
       * How the place looks from anywhere in it, written once and kept. The first local paid test
       * filmed an apartment whose walls were cream in three shots and mint green in five: the
       * plan listed its objects but never its surfaces, and nothing carried them between angles.
       */
      environment: z
        .string()
        .min(1)
        .optional()
        .describe(
          'A detailed description of how the place looks from every angle, the same in every shot: indoors the wall colours and materials, floor, ceiling and fixed fittings; outdoors each building and its colour, trees, the street or ground, and the skyline.',
        ),
      lighting: z
        .string()
        .min(1)
        .describe(
          "The room's fixed light sources and where they are — windows, lamps, fittings — never whether they are on, the time of day or the weather.",
        ),
      anchors: z
        .array(z.object({ id: z.string(), description: z.string(), position: z.string() }))
        .min(1)
        .max(12),
      cameras: z
        .array(
          z.object({
            key: z.string(),
            position: z.string(),
            angle: z.string(),
            framing: z.string(),
            lens: z
              .string()
              .describe(
                'Lens character, e.g. "35mm". A camera move travels only between views with exactly the same lens, so views a move connects share one.',
              ),
          }),
        )
        .min(1)
        .max(MAX_SCENE_CAMERAS),
      /** Pipeline 2: named places people stand, and props whose state can change. */
      marks: z
        .array(
          z.object({
            id: z.string(),
            description: z.string(),
            position: z.string(),
            faces: z.string().optional(),
          }),
        )
        .max(12)
        .optional(),
      /** Pipeline 2: how people are placed relative to each other and the furniture. */
      arrangement: z.string().optional(),
      props: z
        .array(
          z.object({
            id: z.string(),
            description: z.string(),
            initialState: z.string().describe('e.g. "closed", "face down on the vanity".'),
          }),
        )
        .max(12)
        .optional(),
    })
    .optional(),
});
export const ScriptSchema = z
  .object({
    screenplay: z.string(),
    // One to four. It was three to six, which in sixty to ninety seconds is a new place every
    // fifteen seconds: each one to establish, each one a new set for continuity to drift in.
    scenes: z.array(SceneSchema).min(1).max(4),
    shots: z.array(ShotSchema).min(MIN_EPISODE_SHOTS).max(MAX_EPISODE_SHOTS),
    /** What the first three seconds show, the image or line the episode ends on, and its poster. */
    hook: z.string().optional(),
    cliffhanger: z.string().optional(),
    posterShotKey: z.string().optional(),
  })
  .superRefine((plan, ctx) => {
    for (const scene of plan.scenes) {
      if (
        scene.space &&
        (new Set(scene.space.cameras.map((c) => c.key)).size !== scene.space.cameras.length ||
          new Set(scene.space.anchors.map((a) => a.id)).size !== scene.space.anchors.length)
      )
        ctx.addIssue({
          code: 'custom',
          message: `Scene ${scene.key} needs unique camera and anchor IDs.`,
        });
    }
    for (const s of plan.shots) {
      // A Pipeline 2 sequence is one generation of up to fifteen seconds with its own cuts.
      if (s.duration > MAX_GENERATION_SECONDS && !s.takeBeats && !s.cuts)
        ctx.addIssue({
          code: 'custom',
          message: `Shot ${s.key} needs timed take beats for extension.`,
        });
      if (s.takeBeats) {
        if (
          s.duration <= MAX_GENERATION_SECONDS ||
          Math.abs(s.takeBeats.reduce((n, b) => n + b.duration, 0) - s.duration) > 0.01
        )
          ctx.addIssue({
            code: 'custom',
            message: `Shot ${s.key} take beats must cover one long take exactly.`,
          });
        if (JSON.stringify(s.takeBeats.flatMap((b) => b.dialogue)) !== JSON.stringify(s.dialogue))
          ctx.addIssue({
            code: 'custom',
            message: `Shot ${s.key} take beats must preserve every dialogue line in order.`,
          });
        if (s.takeBeats.some((b) => wordsPerSecond(b) > MAX_WORDS_PER_SECOND))
          ctx.addIssue({
            code: 'custom',
            message: `Shot ${s.key} has a take beat with too much dialogue.`,
          });
      }
      if (wordsPerSecond(s) > MAX_WORDS_PER_SECOND)
        ctx.addIssue({
          code: 'custom',
          message: `Shot ${s.key} has more dialogue than it can say in ${s.duration} seconds.`,
        });
      if (s.screenPositions?.some((p) => !s.characterIds.includes(p.characterId)))
        ctx.addIssue({
          code: 'custom',
          message: `Shot ${s.key} places someone who is not in it.`,
        });
      const scene = plan.scenes.find((scene) => scene.key === s.sceneKey);
      if (s.cameraKey && !scene?.space?.cameras.some((c) => c.key === s.cameraKey))
        ctx.addIssue({ code: 'custom', message: `Shot ${s.key} uses an unknown camera.` });
      if (s.cameraMove) {
        const move = s.cameraMove;
        if (!s.cameraKey || !scene?.space)
          ctx.addIssue({
            code: 'custom',
            message: `Shot ${s.key} needs a planned starting camera for its movement.`,
          });
        if (move.endCameraKey && !scene?.space?.cameras.some((c) => c.key === move.endCameraKey))
          ctx.addIssue({
            code: 'custom',
            message: `Shot ${s.key} uses an unknown camera endpoint.`,
          });
        if (move.kind !== 'hold' && (!move.endCameraKey || move.endCameraKey === s.cameraKey))
          ctx.addIssue({
            code: 'custom',
            message: `Shot ${s.key} needs a distinct planned endpoint for its camera movement.`,
          });
        if (move.kind === 'hold' && move.endCameraKey && move.endCameraKey !== s.cameraKey)
          ctx.addIssue({
            code: 'custom',
            message: `Shot ${s.key} cannot change camera view while holding.`,
          });
        const start = scene?.space?.cameras.find((c) => c.key === s.cameraKey);
        const end = scene?.space?.cameras.find((c) => c.key === move.endCameraKey);
        if (start && end && start.lens !== end.lens)
          ctx.addIssue({
            code: 'custom',
            message: `Shot ${s.key} must preserve its lens through a continuous camera movement.`,
          });
      }
      if (
        s.blocking &&
        (new Set(s.blocking.map((b) => b.characterId)).size !== s.blocking.length ||
          s.blocking.some((b) => !s.characterIds.includes(b.characterId)) ||
          s.characterIds.some((id) => !s.blocking!.some((b) => b.characterId === id)))
      )
        ctx.addIssue({
          code: 'custom',
          message: `Shot ${s.key} needs one blocking entry per visible actor.`,
        });
      if (
        scene?.space &&
        (s.location !== scene.location || !s.cameraKey || !s.blocking || !s.cutReason)
      )
        ctx.addIssue({
          code: 'custom',
          message: `Shot ${s.key} needs the scene location, camera, blocking and cut reason.`,
        });
    }
    if (plan.posterShotKey && !plan.shots.some((s) => s.key === plan.posterShotKey))
      ctx.addIssue({ code: 'custom', message: 'The poster shot is not in the episode.' });
    if (
      plan.shots.some(
        (s) => s.generationDuration !== undefined && s.generationDuration < s.duration,
      )
    )
      ctx.addIssue({
        code: 'custom',
        message: 'Source generation must cover the intended edit duration.',
      });
    const total = plan.shots.reduce((n, s) => n + s.duration, 0);
    if (total < 60 || total > 90)
      ctx.addIssue({ code: 'custom', message: 'Episode must total 60–90 seconds.' });
    if (new Set(plan.shots.map((s) => s.key)).size !== plan.shots.length)
      ctx.addIssue({ code: 'custom', message: 'Shot keys must be unique.' });
    if (plan.shots.some((_, i) => !plan.shots.some((s) => s.order === i)))
      ctx.addIssue({ code: 'custom', message: 'Shot order must be unique.' });
    for (const s of plan.shots)
      if (!plan.scenes.some((scene) => scene.key === s.sceneKey))
        ctx.addIssue({ code: 'custom', message: `Unknown scene ${s.sceneKey}` });
  });
export type Script = z.infer<typeof ScriptSchema>;
/**
 * Where a fixed object of the room is, at one moment of a take. `out_of_view` and `occluded` are
 * the camera or the action hiding it for a reason; `changed` and `missing` are the room itself
 * failing — the exit that became blank wall in the Parallel Truths trial was `missing`.
 */
export const ANCHOR_STATES = ['present', 'out_of_view', 'occluded', 'changed', 'missing'] as const;
export const RoomAnchorCheckSchema = z.object({
  id: z.string(),
  start: z.enum(ANCHOR_STATES),
  middle: z.enum(ANCHOR_STATES),
  end: z.enum(ANCHOR_STATES),
  note: z.string().optional(),
});
/**
 * Whether a frame or take keeps its scene: the time of day and weather (in the light and through
 * windows), which lamps are on, and each person's clothes. `not_visible` when the frame cannot
 * show it — no window, nobody dressed in view.
 */
export const SCENE_CHECK = ['holds', 'differs', 'not_visible'] as const;
export const SceneCheckSchema = z.object({
  timeAndWeather: z.enum(SCENE_CHECK),
  lamps: z.enum(SCENE_CHECK),
  clothing: z.enum(SCENE_CHECK),
  /** The place itself — surfaces, colours, materials, landmarks — against its description. */
  surroundings: z.enum(SCENE_CHECK).optional(),
  note: z.string().optional().describe('What differs, when anything does.'),
});
/** What the executor measured itself, rather than what a model judged. */
export const ShotMeasurementsSchema = z.object({
  /** SSIM of the clip's first frame against the approved starting still, 0–1. */
  startSimilarity: z.number().nullable().optional(),
  /** Faces in frames sampled from the retained take, each scored against the locked cast. */
  frames: z
    .array(
      z.object({
        label: z.string(),
        faces: z.array(
          z.object({
            best: z.string().nullable(),
            similarity: z.number().nullable(),
            height: z.number(),
            score: z.number(),
            /** Pipeline 2: which third of the frame the face is in. */
            side: z.enum(['left', 'center', 'right']).optional(),
          }),
        ),
        /** Pipeline 2: when the frame was taken and which planned cut or sequence it shows. */
        at: z.number().optional(),
        cut: z.number().optional(),
        sequence: z.string().optional(),
      }),
    )
    .optional(),
  /** Pipeline 2: the cuts FFmpeg detected in the clip, in source seconds. */
  cuts: z.array(z.number()).optional(),
});
export type ShotMeasurements = z.infer<typeof ShotMeasurementsSchema>;
/**
 * A check's verdict, with its evidence first.
 *
 * The model writes the fields in order, so a schema that opened on `safe` and `passed` had it
 * commit to a verdict before it had written down a single observation. What was heard, the room
 * and the issues now come first, and the verdict follows from them.
 */
export const QaSchema = z.object({
  observedDialogue: z
    .array(
      z.object({
        characterId: z.string(),
        text: z.string(),
        start: z.number().nonnegative(),
        end: z.number().positive(),
      }),
    )
    .optional(),
  roomAnchors: z.array(RoomAnchorCheckSchema).optional(),
  sceneChecks: SceneCheckSchema.optional(),
  issues: z.array(
    z.object({
      severity: z.enum(['info', 'warning', 'critical']),
      category: z.enum([
        'identity',
        'continuity',
        'dialogue',
        'safety',
        'technical',
        'story',
        'performance',
        'voice',
      ]),
      description: z.string(),
      /**
       * The correction as a direction for the next attempt ("Keep the exit door and its green sign
       * on the left wall throughout"), which is what a retake is given instead of the finding.
       */
      fix: z.string().optional(),
    }),
  ),
  summary: z.string(),
  /** Set only when a competing take of the same shot is supplied: whether this one is better. */
  betterThanCompetingTake: z.boolean().optional(),
  measurements: ShotMeasurementsSchema.optional(),
  safe: z.boolean(),
  passed: z.boolean(),
});
export type Qa = z.infer<typeof QaSchema>;
/**
 * What the video check must answer. The room report is required here, so the model has to look at
 * every fixed object through the whole take rather than only around the cut points; the
 * measurements are the executor's and never the model's to write.
 */
export const QaVideoResponseSchema = QaSchema.omit({ measurements: true }).extend({
  roomAnchors: z.array(RoomAnchorCheckSchema),
  sceneChecks: SceneCheckSchema.required({ surroundings: true }),
});
/** What the check of a starting frame must answer: whether it keeps its scene's time and clothes. */
export const QaReferenceResponseSchema = QaSchema.omit({
  measurements: true,
  betterThanCompetingTake: true,
  observedDialogue: true,
}).extend({ sceneChecks: SceneCheckSchema.required({ surroundings: true }) });
export const DOCTOR_CRITERIA = [
  'hook',
  'question',
  'escalation',
  'cliffhanger',
  'economy',
  'subtext',
  'visual',
  'geography',
  'generation',
  'vertical',
] as const;
/**
 * A draft screenplay scored and rewritten before any of it is filmed.
 *
 * Text is the cheap place to fix a story: a second reading costs cents, and every shot it saves
 * from being filmed badly is most of a dollar. `scores` describe the draft, so the record shows
 * what the rewrite was answering.
 */
export const DoctorSchema = z.object({
  scores: z.object(
    Object.fromEntries(DOCTOR_CRITERIA.map((c) => [c, z.number().int().min(1).max(5)])) as Record<
      (typeof DOCTOR_CRITERIA)[number],
      z.ZodNumber
    >,
  ),
  notes: z.array(z.string()).max(12),
  script: ScriptSchema,
});
export type Doctor = z.infer<typeof DoctorSchema>;
/** Notes before scores, so each score follows from what the reviewer has written down. */
export const ScriptReviewSchema = z.object({
  notes: DoctorSchema.shape.notes,
  scores: DoctorSchema.shape.scores,
});
export const scriptPasses = (scores: Record<string, number>) =>
  DOCTOR_CRITERIA.every((c) => scores[c] >= 4);
export const EditPlanSchema = z.object({
  shots: z
    .array(
      z.object({
        shotKey: z.string(),
        trimIn: z.number().nonnegative(),
        reason: z.string().min(1),
      }),
    )
    .min(MIN_EPISODE_SHOTS)
    .max(MAX_EPISODE_SHOTS),
  cues: z
    .array(
      z.object({ shotKey: z.string(), offset: z.number().nonnegative(), cue: z.string().min(1) }),
    )
    .max(12),
});
export type EditPlan = z.infer<typeof EditPlanSchema>;
/**
 * The finished episode watched as a viewer would, once, before it is shown to its creator.
 *
 * Every shot has already been checked on its own. What only the cut reveals is how they sit
 * together: a voice that changes between two shots of the same person, a room that moves, a
 * sound that jumps, an opening that does not grab or an ending that does not pull.
 */
export const EpisodeReviewSchema = z.object({
  issues: z
    .array(
      z.object({
        shotKey: z.string(),
        severity: z.enum(['info', 'warning', 'critical']),
        category: z.enum([
          'identity',
          'voice',
          'continuity',
          'audio',
          'pacing',
          'story',
          'technical',
          'performance',
        ]),
        description: z.string(),
        /** The correction as a direction for a retake of that shot. */
        fix: z.string().optional(),
      }),
    )
    .max(20),
  summary: z.string(),
  hookLands: z.boolean(),
  cliffhangerLands: z.boolean(),
});
export type EpisodeReview = z.infer<typeof EpisodeReviewSchema>;
export const RevisionSchema = z.object({
  summary: z.string(),
  shotKeys: z
    .array(z.string())
    .min(1)
    .describe('Only the keys of the shots you changed, one for each entry of updatedShots.'),
  updatedShots: z.array(ShotSchema).min(1),
  cameraAdditions: z
    .array(
      z.object({
        sceneKey: z.string(),
        camera: SceneSchema.shape.space.unwrap().shape.cameras.element,
      }),
    )
    .max(MAX_SCENE_CAMERAS)
    .optional()
    .describe(
      'New camera views needed by revised shots. Use new keys; never redefine an existing camera or room.',
    ),
});
/**
 * A shot said again, in words a content filter will accept.
 *
 * The provider refuses on wording, not on story: a plane crash survivor "blinking blood and wood
 * dust from his eyes" was declined, and nothing about the scene is objectionable. The creator
 * never wrote that line — the script model did — so the system rewrites its own prose rather than
 * handing them an artifact they did not choose the words of.
 *
 * Only the prose may move. Duration, order, scene, cast and dialogue are the episode's structure:
 * a rewrite that shortened a shot would silently change the episode's length, and one that
 * dropped a character would break the continuity the next shot depends on. Those are checked in
 * `resultValidation`, against the shot as it stands, because the schema alone cannot know what
 * the shot used to be.
 */
export const SoftenSchema = z.object({
  reason: z.string(),
  shotKey: z.string(),
  spec: ShotSchema,
});

export const CaptionSchema = z.object({
  cues: z.array(
    z.object({ start: z.number().nonnegative(), end: z.number().positive(), text: z.string() }),
  ),
});
export const AssetSchema = z.object({
  key: z.string().min(1),
  mime: z.enum([
    'image/png',
    'image/jpeg',
    'video/mp4',
    'audio/wav',
    'text/vtt',
    'application/vnd.apple.mpegurl',
    'video/mp2t',
  ]),
  filename: z
    .string()
    .regex(/^[a-zA-Z0-9_-]+\.(m3u8|ts)$/)
    .optional(),
  bytes: z.number().int().positive().max(100_000_000),
  duration: z.number().positive().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
});
export type AssetOutput = z.infer<typeof AssetSchema>;
export const JobOutputSchema = z.object({
  providerTrace: ProviderTraceSchema.optional(),
  data: z.unknown().optional(),
  assets: z.array(AssetSchema).max(20).default([]),
  costMicros: z.number().int().nonnegative(),
  costBasis: z.enum(['usage', 'estimate', 'simulated']),
  model: z.string(),
  usage: z.record(z.string(), z.number()).optional(),
});
export type JobOutput = z.infer<typeof JobOutputSchema>;
export const CommandInputSchema = z.object({
  providerProfile: z
    .string()
    .regex(/^(google-default|cloudflare-[a-z0-9-]+)$/)
    .max(80)
    .optional(),
  requestId: z.string().uuid(),
  kind: z.enum(commandKinds),
  projectId: z.string(),
  episodeId: z.string().optional(),
  shotId: z.string().optional(),
  characterId: z.string().optional(),
  instruction: z.string().max(4000).optional(),
  reuseReference: z.boolean().optional(),
  recheckReference: z.boolean().optional(),
  recheckVideo: z.boolean().optional(),
  roomToneShotId: z.string().optional(),
  assembleWhenReady: z.boolean().optional(),
  maxCostMicros: z.number().int().nonnegative().optional(),
  videoModel: z.enum([MODELS.video, MODELS.controlledVideo]).optional(),
});
export type CommandInput = z.infer<typeof CommandInputSchema>;
export interface JobPayload {
  routing?: Routing;
  projectId: string;
  episodeId?: string;
  shotId?: string;
  characterId?: string;
  instruction?: string;
  model: string;
  context: Record<string, unknown>;
  assets?: { key: string; url: string; mime: string }[];
}
export interface ClaimedJob {
  id: string;
  token: string;
  kind: JobKind;
  payload: JobPayload;
  reservationMicros: number;
  operation?: import('./provider-contracts').ProviderOperation;
  /** Who and which command this work belongs to, so its generation records can be joined back. */
  trace?: {
    commandId: string;
    distinctId: string;
    commandKind: string;
    attempt?: number;
    attemptId?: string;
  };
}
export const money = (micros: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(micros / 1_000_000);
export const normalizeEmail = (email: string) => email.trim().toLowerCase();
export function canEdit(role: Role) {
  return role === 'owner' || role === 'editor';
}
export function assertBudget(spent: number, reserved: number, amount: number, cap = BUDGET_CAP) {
  if (![spent, reserved, amount, cap].every((n) => Number.isSafeInteger(n) && n >= 0))
    throw new Error('Invalid monetary amount.');
  if (spent + reserved + amount > cap)
    throw new Error('Budget limit reached. No generation was submitted.');
}
export function reserveFor(kind: JobKind, model: string, duration?: number): number {
  // Upper allowances, not advertised prices. Include bounded inputs and output/thinking.
  if (kind === 'ASSEMBLE') return 0;
  if (kind === 'SPEECH') return 500000;
  if (kind === 'VIDEO') {
    if (model === MODELS.controlledVideo) return 6_000_000;
    // Six-second 720p Omni runs cost ~ $0.623 in the pilot, about $0.104 a second, and the
    // published price is $0.10 a second. Hold $0.22 a generated second — more than twice either —
    // with a floor of $1.30. It used to be a step, $1.30 up to six seconds and $2 above, which
    // priced a seven-second clip as a ten-second one; now that every shot is generated half a
    // second long for the edit to cut past, most shots sit just above that step. Unknown
    // duration holds the longest clip the generator is asked for.
    // This is an allowance, not a provider-enforced billing maximum.
    return Math.max(
      1_300_000,
      Math.ceil(duration && duration > 0 ? duration : MAX_GENERATION_SECONDS) * 220_000,
    );
  }
  if (kind === 'CAST_IMAGE' || kind === 'REFERENCE' || kind === 'SET_IMAGE') return 700_000;
  // Full-frame-rate episode previews carry many more input tokens than a single shot.
  if (kind === 'EPISODE_REVIEW' || kind === 'EDIT_PLAN') return 1_500_000;
  // QA output is capped at 12k tokens; $0.40 covers output plus bounded
  // context/media input with headroom. Transcription is limited to 90s audio
  // and 2k output tokens (~$0.029 maximum at the recorded token rates).
  if (
    kind === 'QA_VIDEO' ||
    kind === 'QA_REFERENCE' ||
    kind === 'QA_SET' ||
    kind === 'QA_CONTENT' ||
    kind === 'SOFTEN' ||
    kind === 'SCRIPT_REVIEW' ||
    kind === 'PANEL' ||
    kind === 'QA_PERFORMANCE'
  )
    return 400_000;
  if (kind === 'CAPTIONS') return 100_000;
  // Lyria is priced per song, $0.08 for Lyria 3.5 on September 28, 2026. Twice that.
  if (kind === 'SCORE') return 160_000;
  // Bound writing and doctor attempts; the quote also reserves independent reviews and repair.
  if (
    kind === 'SCRIPT' ||
    kind === 'SCRIPT_DOCTOR' ||
    kind === 'STORY' ||
    kind === 'PUNCH_UP' ||
    kind === 'BREAKDOWN'
  )
    return 750_000;
  return 1_500_000;
}
export function visibleCanon<T extends { validFrom: number; knownFrom: number }>(
  facts: T[],
  episode: number,
) {
  return facts.filter((f) => f.validFrom <= episode && f.knownFrom <= episode);
}
export function validateCaptions(cues: z.infer<typeof CaptionSchema>['cues'], duration: number) {
  let previousEnd = 0;
  for (const c of cues) {
    if (c.start < previousEnd || c.end <= c.start || c.end > duration + 0.05)
      throw new Error('Captions overlap or exceed episode duration.');
    previousEnd = c.end;
  }
  return cues;
}
export function webVtt(cues: z.infer<typeof CaptionSchema>['cues']) {
  const time = (v: number) => {
    const ms = Math.round(v * 1000);
    return `${String(Math.floor(ms / 3600000)).padStart(2, '0')}:${String(Math.floor(ms / 60000) % 60).padStart(2, '0')}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')}.${String(ms % 1000).padStart(3, '0')}`;
  };
  return (
    'WEBVTT\n\n' +
    cues
      .map(
        (c, i) =>
          `${i + 1}\n${time(c.start)} --> ${time(c.end)}\n${c.text.replace(/-->/g, '→').replace(/[<>]/g, '')}\n`,
      )
      .join('\n')
  );
}
export const EXPERIMENT_IDEAS = [
  [
    'The Last Table',
    'Romance',
    'A chef returns home to sell her late mother’s restaurant. The buyer is the man she left at the altar. A reservation in her mother’s handwriting forces them to spend one final evening together.',
  ],
  [
    'Room 407',
    'Mystery',
    'A night receptionist receives a call from a room sealed twenty years ago. The caller knows her childhood nickname. When she opens the guest register, she finds tomorrow’s date beside her own signature.',
  ],
  [
    'Everything We Kept',
    'Family drama',
    'Three adult siblings reunite to empty their childhood home. A box of unopened letters reveals their father had another life. Each letter names a different sibling as the only person who knew.',
  ],
  [
    'Borrowed Light',
    'Romance',
    'Two neighbors communicate through notes left in a broken elevator. Each is hiding a recent heartbreak. When the elevator is repaired, they must decide whether to finally meet.',
  ],
  [
    'The Quiet Witness',
    'Crime',
    'A courthouse cleaner finds a voice recording hidden beneath a bench. It contradicts tomorrow’s star witness. The voice belongs to her brother.',
  ],
  [
    'Last Train North',
    'Thriller',
    'A woman boards the last train with an anonymous envelope. Every passenger receives a photograph of her. At the next station, the conductor locks the doors.',
  ],
  [
    'A Perfect Alibi',
    'Mystery',
    'A wedding photographer notices the same stranger in twenty years of family photographs. Nobody remembers inviting him. At her own wedding, he introduces himself as her son.',
  ],
  [
    'The Spare Key',
    'Comedy',
    'Two strangers inherit the same apartment from someone neither remembers. Both arrive with a valid key and a moving van. The will says they must host a dinner together to discover why.',
  ],
  [
    'After the Applause',
    'Family drama',
    'An aging stage actor forgets his final monologue. His estranged daughter whispers it from the wings. The words reveal why she left ten years ago.',
  ],
  [
    'Blue Hour',
    'Romance',
    'A florist delivers an apology bouquet to the wrong apartment. The recipient writes back. After a month of letters, the intended recipient appears at the shop.',
  ],
].map(([title, genre, idea], i) => ({ id: `concept-${i + 1}`, title, genre, idea }));
